import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue, 
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { Mail, Phone, ArrowRight, CheckCircle2, Loader2 } from 'lucide-react';
import SEO, { getLocalBusinessSchema } from '../components/SEO';
import { trackContactFormStarted, trackContactFormCompleted } from '../lib/posthog';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Service options mapping for query params
const SERVICE_PARAM_MAP = {
  'ai-automation': 'ai-systems',
  'website': 'website-design',
  'real-estate': 'real-estate',
  'portraits': 'couples',
};

const formSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters." }),
  phone: z.string().min(10, { message: "Please enter a valid phone number." }),
  email: z.string().email({ message: "Please enter a valid email address." }),
  company: z.string().optional(),
  service: z.string({ required_error: "Please select a service." }),
  method: z.enum(["text", "phone", "email"], { required_error: "Please select a contact method." }),
  message: z.string().min(10, { message: "Message must be at least 10 characters." }),
});

const Contact = () => {
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Get service from query params
  const serviceParam = searchParams.get('service');
  const defaultService = serviceParam ? SERVICE_PARAM_MAP[serviceParam] || '' : '';
  const [formStarted, setFormStarted] = useState(false);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      phone: "",
      email: "",
      company: "",
      message: "",
      service: defaultService,
    },
  });

  // Update service field when URL param changes
  useEffect(() => {
    if (defaultService) {
      form.setValue('service', defaultService);
    }
  }, [defaultService, form]);

  // Track when user starts filling out the form
  const handleFormInteraction = () => {
    if (!formStarted) {
      setFormStarted(true);
      trackContactFormStarted(form.getValues('service'));
    }
  };

  async function onSubmit(values) {
    setIsSubmitting(true);
    try {
      // Track form completion (service only, no PII)
      trackContactFormCompleted(values.service);
      
      // Map form values to backend schema
      const payload = {
        name: values.name,
        email: values.email,
        subject: values.service ? `${values.service}${values.company ? ` - ${values.company}` : ''}` : null,
        message: `${values.message}\n\n---\nPhone: ${values.phone}\nPreferred contact: ${values.method}${values.company ? `\nCompany: ${values.company}` : ''}`
      };

      const response = await fetch(`${API_BASE}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.status === 429) {
        toast.error("Too many requests. Please try again later.");
        return;
      }

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to submit form');
      }

      setIsSuccess(true);
      toast.success("Message received — we'll reply shortly.");
      form.reset();
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error(error.message || "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  // Success state UI
  if (isSuccess) {
    return (
      <div className="flex flex-col min-h-screen">
        {/* Hero Section - Clean Image Only */}
        <section className="hero-main relative h-[400px] md:h-[480px] lg:h-[520px] overflow-hidden">
          <div className="absolute inset-0">
            <img 
              src="https://customer-assets.emergentagent.com/job_rcstudioapp/artifacts/2s4ymc2z_Contact-hero.jpg"
              alt="RC Studios contact"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-[#050816]/50 to-transparent"></div>
          </div>
        </section>

        {/* Success Message */}
        <section className="py-20 bg-[#FAF9F6] flex-1">
          <div className="container mx-auto px-6 max-w-[600px] text-center">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-12">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl md:text-3xl font-serif font-bold text-foreground mb-4">
                Message Received!
              </h2>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                Thank you for reaching out. I personally review every message and will get back to you within 24 hours on business days.
              </p>
              <Button 
                onClick={() => setIsSuccess(false)}
                variant="outline"
                className="mr-4"
              >
                Send Another Message
              </Button>
              <Link to="/">
                <Button className="bg-[#050816] hover:bg-[#050816]/90">
                  Back to Home
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      {/* SEO Meta Tags */}
      <SEO
        title="Contact Us | Get a Quote"
        description="Contact RC Studios for photography, video production, and AI media services. Based in Kingman, Arizona, serving Mohave County and nationwide. Get a free quote today."
        path="/contact"
        jsonLd={getLocalBusinessSchema()}
      />

      {/* Hero Section - Clean Image Only */}
      <section className="hero-main relative h-[400px] md:h-[480px] lg:h-[520px] overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_rcstudioapp/artifacts/2s4ymc2z_Contact-hero.jpg"
            alt="RC Studios contact"
            className="w-full h-full object-cover object-center"
          />
          {/* Subtle cinematic vignette for navbar contrast only */}
          <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-[#050816]/50 to-transparent"></div>
        </div>
      </section>

      {/* Breadcrumb Section */}
      <section className="bg-[#FAF9F6] border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6 py-5 md:py-6">
          <div className="flex items-center gap-3">
            <Link 
              to="/contact" 
              className="text-[11px] font-medium tracking-[0.2em] uppercase text-[#F5C056] hover:text-[#F5C056]/80 transition-colors"
            >
              Contact
            </Link>
            <span className="text-[#050816]/30 text-xs">/</span>
            <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-[#050816]">
              Get In Touch
            </span>
          </div>
          <div className="w-12 h-[1.5px] bg-[#F5C056]/40 mt-4"></div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-[#FAF9F6]">
        <div className="container mx-auto px-6 max-w-[1200px]">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
            
            {/* Left Column: Form */}
            <div className="lg:col-span-7 relative">
              {/* Soft visual treatment behind form */}
              <div className="absolute -inset-4 bg-gradient-to-br from-[#050816]/[0.03] via-transparent to-transparent rounded-3xl pointer-events-none"></div>
              <div className="absolute -inset-4 opacity-30 pointer-events-none" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.8\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")', opacity: 0.02}}></div>
              
              {/* Intro Block */}
              <div className="relative mb-8">
                <h2 className="text-2xl md:text-3xl font-serif font-bold text-foreground mb-3">
                  Let&apos;s Talk About Your Idea
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  Whether you have a clear plan or just a spark, I&apos;d love to hear what you&apos;re thinking.
                </p>
              </div>
              
              {/* Form Card with Gold Accent */}
              <div className="relative bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                {/* Gold accent line at top */}
                <div className="h-1 bg-gradient-to-r from-[#F5C056]/80 via-[#F5C056] to-[#F5C056]/80"></div>
                
                <div className="p-8 md:p-10">
                  <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-7">
                      
                      {/* Basic Info */}
                      <div className="grid md:grid-cols-2 gap-6">
                        <FormField
                          control={form.control}
                          name="name"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium text-gray-700">Your Name <span className="text-[#F5C056] font-bold">*</span></FormLabel>
                              <FormControl>
                                <Input 
                                  placeholder="What should I call you?" 
                                  {...field} 
                                  onFocus={handleFormInteraction}
                                  className="h-12 bg-gray-50/50 border-gray-200 focus:border-[#F5C056] focus:ring-2 focus:ring-[#F5C056]/20 focus:bg-white transition-all" 
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="phone"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium text-gray-700">Best Number <span className="text-[#F5C056] font-bold">*</span></FormLabel>
                              <FormControl>
                                <Input placeholder="Where can I reach you?" {...field} className="h-12 bg-gray-50/50 border-gray-200 focus:border-[#F5C056] focus:ring-2 focus:ring-[#F5C056]/20 focus:bg-white transition-all" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <FormField
                          control={form.control}
                          name="email"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium text-gray-700">Email <span className="text-[#F5C056] font-bold">*</span></FormLabel>
                              <FormControl>
                                <Input placeholder="your@email.com" {...field} className="h-12 bg-gray-50/50 border-gray-200 focus:border-[#F5C056] focus:ring-2 focus:ring-[#F5C056]/20 focus:bg-white transition-all" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="company"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium text-gray-700">Company or Brand <span className="text-gray-400 text-xs font-normal">(if applicable)</span></FormLabel>
                              <FormControl>
                                <Input placeholder="Your business name" {...field} className="h-12 bg-gray-50/50 border-gray-200 focus:border-[#F5C056] focus:ring-2 focus:ring-[#F5C056]/20 focus:bg-white transition-all" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Service Selection */}
                      <FormField
                        control={form.control}
                        name="service"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-gray-700">What brings you here? <span className="text-[#F5C056] font-bold">*</span></FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger className="h-12 bg-gray-50/50 border-gray-200 focus:border-[#F5C056] focus:ring-2 focus:ring-[#F5C056]/20 focus:bg-white transition-all">
                                  <SelectValue placeholder="Choose what fits best" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="real-estate">Real Estate Photography</SelectItem>
                                <SelectItem value="couples">Couples / Engagement</SelectItem>
                                <SelectItem value="weddings">Weddings</SelectItem>
                                <SelectItem value="boudoir">Boudoir / Private Collection</SelectItem>
                                <SelectItem value="travel">Travel / Dedicated Photographer</SelectItem>
                                <SelectItem value="website-design">Website & Platform Development</SelectItem>
                                <SelectItem value="ai-systems">AI Systems & Automation</SelectItem>
                                <SelectItem value="digital-persona">AI Content & Digital Personas</SelectItem>
                                <SelectItem value="other">Something else entirely</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {/* Contact Preference */}
                      <FormField
                        control={form.control}
                        name="method"
                        render={({ field }) => (
                          <FormItem className="space-y-3">
                            <FormLabel className="text-sm font-medium text-gray-700">How should I reach out? <span className="text-[#F5C056] font-bold">*</span></FormLabel>
                            <FormControl>
                              <RadioGroup
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                                className="flex flex-col space-y-2"
                              >
                                <div className="flex items-center space-x-3 space-y-0 p-3 rounded-lg hover:bg-gray-50/50 transition-colors cursor-pointer">
                                  <FormControl>
                                    <RadioGroupItem value="text" className="text-[#F5C056] border-gray-300 data-[state=checked]:border-[#F5C056]" />
                                  </FormControl>
                                  <FormLabel className="font-normal text-gray-600 cursor-pointer">Text message — quick and easy</FormLabel>
                                </div>
                                <div className="flex items-center space-x-3 space-y-0 p-3 rounded-lg hover:bg-gray-50/50 transition-colors cursor-pointer">
                                  <FormControl>
                                    <RadioGroupItem value="phone" className="text-[#F5C056] border-gray-300 data-[state=checked]:border-[#F5C056]" />
                                  </FormControl>
                                  <FormLabel className="font-normal text-gray-600 cursor-pointer">Phone call — let&apos;s chat</FormLabel>
                                </div>
                                <div className="flex items-center space-x-3 space-y-0 p-3 rounded-lg hover:bg-gray-50/50 transition-colors cursor-pointer">
                                  <FormControl>
                                    <RadioGroupItem value="email" className="text-[#F5C056] border-gray-300 data-[state=checked]:border-[#F5C056]" />
                                  </FormControl>
                                  <FormLabel className="font-normal text-gray-600 cursor-pointer">Email — I&apos;ll check it regularly</FormLabel>
                                </div>
                              </RadioGroup>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {/* Message */}
                      <FormField
                        control={form.control}
                        name="message"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-gray-700">Tell me more <span className="text-[#F5C056] font-bold">*</span></FormLabel>
                            <FormControl>
                              <Textarea 
                                placeholder="Share the details — your vision, timeline, questions, or anything else on your mind..." 
                                className="min-h-[140px] resize-none bg-gray-50/50 border-gray-200 focus:border-[#F5C056] focus:ring-2 focus:ring-[#F5C056]/20 focus:bg-white transition-all" 
                                {...field} 
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <Button 
                        type="submit" 
                        disabled={isSubmitting}
                        className="w-full h-14 text-base font-bold bg-[#050816] hover:bg-[#050816]/90 text-white rounded-xl shadow-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed"
                        data-testid="contact-submit-btn"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            Send Message
                            <ArrowRight className="ml-2 w-5 h-5" />
                          </>
                        )}
                      </Button>
                      
                      {/* Reassurance Line */}
                      <p className="text-sm text-center text-muted-foreground/70 pt-2">
                        I personally review every message. No bots. No pressure.
                      </p>
                    </form>
                  </Form>
                </div>
              </div>
            </div>

            {/* Right Column: Info */}
            <div className="lg:col-span-5 space-y-8">
              
              {/* Contact Details */}
              <div className="space-y-6">
                <h3 className="text-xl font-serif font-bold text-foreground">Get in Touch Directly</h3>
                <div className="space-y-4">
                  <a href="mailto:marketing@rcstudios.net" className="flex items-start gap-4 group p-4 rounded-xl hover:bg-white hover:shadow-md transition-all duration-300 border border-transparent hover:border-gray-100">
                    <div className="w-12 h-12 rounded-full bg-[#F5C056]/10 flex items-center justify-center text-[#F5C056] group-hover:bg-[#F5C056] group-hover:text-white transition-all duration-300 group-hover:scale-105">
                      <Mail className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-1">Email</p>
                      <p className="text-lg text-muted-foreground group-hover:text-[#F5C056] transition-colors">marketing@rcstudios.net</p>
                    </div>
                  </a>

                  <a href="tel:9285306604" className="flex items-start gap-4 group p-4 rounded-xl hover:bg-white hover:shadow-md transition-all duration-300 border border-transparent hover:border-gray-100">
                    <div className="w-12 h-12 rounded-full bg-[#F5C056]/10 flex items-center justify-center text-[#F5C056] group-hover:bg-[#F5C056] group-hover:text-white transition-all duration-300 group-hover:scale-105">
                      <Phone className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-1">Phone</p>
                      <p className="text-lg text-muted-foreground group-hover:text-[#F5C056] transition-colors">(928) 530-6604</p>
                      <p className="text-xs text-muted-foreground/60 mt-1">Prefer texting? Just say so in the form.</p>
                    </div>
                  </a>
                </div>
              </div>

              {/* What Happens Next */}
              <div className="bg-[#050816] text-white p-8 rounded-2xl shadow-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-[250px] h-[250px] bg-[#F5C056]/8 rounded-full blur-[80px] pointer-events-none"></div>
                <div className="absolute bottom-0 left-0 w-[150px] h-[150px] bg-[#F5C056]/5 rounded-full blur-[60px] pointer-events-none"></div>
                
                <h3 className="text-xl font-serif font-bold mb-8 relative z-10">What Happens Next</h3>
                <div className="space-y-6 relative z-10">
                  <div className="flex gap-4 group cursor-default">
                    <div className="w-10 h-10 rounded-full bg-[#F5C056]/20 flex items-center justify-center text-[#F5C056] shrink-0 group-hover:bg-[#F5C056]/30 group-hover:scale-110 transition-all duration-300">
                      <CheckCircle2 className="w-5 h-5" />
                    </div>
                    <div className="group-hover:translate-x-1 transition-transform duration-300">
                      <p className="font-semibold mb-1">I&apos;ll read your message</p>
                      <p className="text-sm text-gray-400 leading-relaxed">Every inquiry is personally reviewed — no auto-replies here.</p>
                    </div>
                  </div>
                  <div className="flex gap-4 group cursor-default">
                    <div className="w-10 h-10 rounded-full bg-[#F5C056]/20 flex items-center justify-center text-[#F5C056] shrink-0 group-hover:bg-[#F5C056]/30 group-hover:scale-110 transition-all duration-300">
                      <CheckCircle2 className="w-5 h-5" />
                    </div>
                    <div className="group-hover:translate-x-1 transition-transform duration-300">
                      <p className="font-semibold mb-1">Quick response time</p>
                      <p className="text-sm text-gray-400 leading-relaxed">Expect to hear back within 24 hours on business days.</p>
                    </div>
                  </div>
                  <div className="flex gap-4 group cursor-default">
                    <div className="w-10 h-10 rounded-full bg-[#F5C056]/20 flex items-center justify-center text-[#F5C056] shrink-0 group-hover:bg-[#F5C056]/30 group-hover:scale-110 transition-all duration-300">
                      <CheckCircle2 className="w-5 h-5" />
                    </div>
                    <div className="group-hover:translate-x-1 transition-transform duration-300">
                      <p className="font-semibold mb-1">We&apos;ll figure it out together</p>
                      <p className="text-sm text-gray-400 leading-relaxed">We&apos;ll talk through your vision and see if it&apos;s a good fit.</p>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;